import React, { useState } from 'react';
import TextView from '../../TextView';
import { ASSETS } from '../../../utils/config';
import { NOTIFICATION_TYPES, getNotificationRedirectUrl, isWheelNotification } from '../../../utils/notificationTypes';
import {
  NotificationContainer,
  LeftSection,
  ImageContainer,
  ProfileImage,
  RightSection,
  WheelImage,
  WheelSlotSpacer,
  TextContainer,
  IconOverlay,
  ActionButtonsContainer,
  StyledButton,
  ActorActionsSlot,
  RightActionsColumn,
  GreenDot,
} from './NotificationItem.styled';
import { useNavigate } from 'react-router-dom';
import { renderSafeDescription, renderNotificationSentence, inertHtmlToText } from '../notificationText';
import UserImage from '../../UserImage';
import UserActions from '../../UserActions';
import NotificationRowMenu from './NotificationRowMenu';
import useWordSafeLineClamp from '../../../hooks/useWordSafeLineClamp';
import useRelativeTime from '../../../hooks/useRelativeTime';
import {
  AVATAR_WHEEL_FRAMES_ENABLED,
  NOTIF_PLAIN_NAME_ENABLED,
  USER_ACTIONS_ENABLED,
} from '../../../constants/featureFlags';

// Mapping type to icons using NOTIFICATION_TYPES
const iconMap = {
  [NOTIFICATION_TYPES.CLONE.value]: (
    <img src={ASSETS.notification_clone} alt="clone" style={{ width: '100%', height: '100%' }} />
  ),
  [NOTIFICATION_TYPES.SHARE.value]: (
    <img src={ASSETS.notification_share} alt="share" style={{ width: '100%', height: '100%' }} />
  ),
  [NOTIFICATION_TYPES.LIVE_CLONE.value]: (
    <img
      src={ASSETS.notification_live_clone}
      alt="liveclone"
      style={{ width: '100%', height: '100%' }}
    />
  ),
  [NOTIFICATION_TYPES.FAVORITE.value]: (
    <img src={ASSETS.notification_heart} alt="like" style={{ width: '100%', height: '100%' }} />
  ),
  [NOTIFICATION_TYPES.LOCK.value]: (
    <img src={ASSETS.notification_lock} alt="lock" style={{ width: '100%', height: '100%' }} />
  ),
  [NOTIFICATION_TYPES.FOLLOW.value]: (
    <img src={ASSETS.notification_follow} alt="follow" style={{ width: '100%', height: '100%' }} />
  ),
  [NOTIFICATION_TYPES.UNLOCK.value]: (
    <img src={ASSETS.notification_unlock} alt="unlock" style={{ width: '100%', height: '100%' }} />
  ),
  // N-03 (measured live, ov-close-notif 2026-07-26): comment rows rendered with NO
  // type badge because COMMENT (16) was never mapped here, so `iconMap[type]` was
  // undefined and the IconOverlay never rendered. Reuse the existing chat-bubble
  // badge asset (notification_message.svg — a 21x20 circular badge matching the
  // heart/clone badge style, already imported/exported in config) so a comment
  // notification carries the same type-badge treatment as every other wheel type.
  [NOTIFICATION_TYPES.COMMENT.value]: (
    <img src={ASSETS.notification_message} alt="comment" style={{ width: '100%', height: '100%' }} />
  ),
  // ISP-9999 (1100a): dedicated collab-icon asset now exists
  // (notification_collab.svg — a two-person line-art badge, matching the
  // circle-badge/glyph convention every other notification icon uses, never
  // emoji). Replaces the earlier notification_follow reuse, which visually
  // conflated a collaboration invite with a single-person follow event.
  [NOTIFICATION_TYPES.COLLABORATION_INVITE.value]: (
    <img src={ASSETS.notification_collab} alt="collaboration invite" style={{ width: '100%', height: '100%' }} />
  ),
  // ISP-9999 (collab-link-join-no-notification): reuses the same collab badge
  // asset as COLLABORATION_INVITE -- both are collaboration-themed events,
  // just addressed to a different audience (owner vs invitee). alt text
  // distinguishes the two for a11y + assertion.
  [NOTIFICATION_TYPES.COLLABORATION_JOINED.value]: (
    <img src={ASSETS.notification_collab} alt="collaboration joined" style={{ width: '100%', height: '100%' }} />
  ),
  // QA-1255 (🟠): WHEEL_REACTION (17) is a wheel-instance notification (it is in
  // WHEEL_INSTANCE_TYPES), so per the documented intent every wheel-type row
  // carries a type badge — but reaction(17) had NO iconMap entry, so `iconMap[type]`
  // was undefined and the IconOverlay never rendered, unlike favorite/clone/follow/
  // comment(16)/collab(18). No reaction-specific badge asset exists; the type's own
  // NOTIFICATION_TYPES.WHEEL_REACTION.icon is 'heart' and a reaction is a heart
  // event, so reuse the existing notification_heart badge (same asset FAVORITE uses,
  // 21x20 circular badge matching the row's badge style). alt="reaction"
  // distinguishes it from the FAVORITE "like" badge for a11y + assertion.
  [NOTIFICATION_TYPES.WHEEL_REACTION.value]: (
    <img src={ASSETS.notification_heart} alt="reaction" style={{ width: '100%', height: '100%' }} />
  ),
};

// Image mapping for special types using NOTIFICATION_TYPES
const specialTypeImages = {
  [NOTIFICATION_TYPES.WARNING.value]: ASSETS.notification_warning,
  [NOTIFICATION_TYPES.STORAGE_WARNING.value]: ASSETS.notification_warning,
  [NOTIFICATION_TYPES.TAX.value]: ASSETS.notification_tax,
  [NOTIFICATION_TYPES.AUTO_DEPOSIT.value]: ASSETS.notification_autodeposit,
  [NOTIFICATION_TYPES.RED_STORAGE.value]: ASSETS.notification_red_storage,
  [NOTIFICATION_TYPES.YELLOW_STORAGE.value]: ASSETS.notification_yellow_storage,
  [NOTIFICATION_TYPES.TIP.value]: ASSETS.notification_bulb,
};

function NotificationItem({
  name,
  image,
  wheel,
  text,
  type,
  wheelName,
  time,
  // kelly-notif-stale-time-ago-0915: when the caller has the raw timestamp,
  // prefer a live client-computed age over the server's one-shot `time_ago`
  // string so the row keeps ticking forward instead of freezing until the
  // component remounts. Optional -- a caller/test that only passes `time`
  // keeps rendering that string exactly as before.
  createdAt,
  isDropDown,
  isUnread,
  showActionButtons,
  wheelId,
  // BE #1434: the notification serializer now carries the actor's own avatar +
  // id + plain-text name. Consumers always pass these; each new field is READ
  // only behind its own flag (AVATAR_WHEEL_FRAMES for avatar/link, USER_ACTIONS
  // for follow/message, NOTIF_PLAIN_NAME for the sentence) so every OFF path stays
  // byte-identical and never touches the new fields.
  actorProfileImage,
  actorUserId,
  actorName,
  // P5S1-O12: optional callback the container passes to persist THIS row's read
  // state (server mark-as-read + local view update) when the row is activated.
  // Optional so every existing caller/test that omits it keeps today's behaviour
  // (navigate only) byte-identical.
  onActivate,
  // ISP-9999 (60-D): raw notification.extras JSON, only read by
  // getNotificationRedirectUrl for COLLABORATION_INVITE (extras.token). Optional
  // -- every existing caller/test that omits it is unaffected (undefined extras
  // just falls through to the wheel-instance route, same as before this change).
  extras,
  // QA-1014 (FB-style redesign): the notification's own id + the container's
  // per-row mutation handlers for the kebab menu. All optional — a caller/test
  // that omits them simply renders a kebab whose Delete/Mark-as-read are inert
  // no-ops (byte-identical to pre-QA-1014 for the OFF/omitted path).
  notificationId,
  onMarkRead,
  onDelete,
  // QA-1014: report handler supplied by the container (which owns the redux
  // report-modal wiring). Kept as a plain callback so this row stays a pure
  // presentational component with no redux Provider dependency.
  onReport,
}) {
  // EW-nemz512: `type` can arrive null/undefined from the feed (older rows, or a
  // field-name mismatch upstream). Guard before .toString() so a single malformed
  // row can't crash the whole notification list.
  const isSpecialType =
    type != null && Object.keys(specialTypeImages).includes(type.toString());
  const displayImage = isSpecialType ? specialTypeImages[type] : image;
  // (`showWheelImage` lived here and had no remaining reader — the thumbnail is
  // gated on isWheelNotification(type) since N-03. Removed; lint flagged it.)
  const isWarning = type === NOTIFICATION_TYPES.WARNING.value;

  // kelly-notif-stale-time-ago-0915: prefer the live, ticking age computed from
  // createdAt; fall back to the server's static `time` string when createdAt
  // is absent (e.g. existing tests that only pass `time`).
  const liveTime = useRelativeTime(createdAt);
  const displayTime = createdAt ? liveTime : time;

  // notif-avatar-broken (Gordon, live staging, 2026-07-20): `profile_picture` can
  // be null/empty for some notification actors (nullable BE field) with no FE
  // fallback -> renders a broken-image glyph.
  // QA-1015 (Gordon, live staging main.f1097468.js, 2026-08-24): the empty/missing-
  // avatar fallback previously used ASSETS.userPlaceholder (an unrelated S3 "TEXTS"
  // asset), which is NOT the app's standard default. Every other default-avatar
  // surface (WheelCard, CollectionCard, ExploreWheelCard, MobileViewDialog, chat
  // WheelPicker, …) degrades a missing portrait to ASSETS.wheelLogoFallback — the
  // canonical iYuga logo (DefaultIyugaLogo / iyugalogo.svg). Reuse that SAME shared
  // asset here so an avatar-less notification shows the iYuga logo placeholder,
  // consistent with the rest of the app, for both the null/empty case and the
  // truthy-but-dead URL (403/404/network fail) that the ternary alone can't see.
  const avatarSrc = displayImage || ASSETS.wheelLogoFallback;
  const handleAvatarError = (e) => {
    e.target.onerror = null;
    e.target.src = ASSETS.wheelLogoFallback;
  };

  // notif-wheel-thumb-broken (N7.17, live staging 2026-07-26): the actor avatar
  // now has an onError fallback, but the right-hand WHEEL THUMBNAIL (`wheel` =
  // notification.image) still had none — a dead thumbnail URL (a deleted/cloned
  // wheel 404s, and cloned-wheel deep-links are ~92% dead-ends) rendered a broken-
  // image glyph. Fall back to the canonical wheel placeholder, mirroring the
  // avatar fix, so a missing thumbnail degrades to the brand wheel mark not a
  // browser broken-image icon.
  const [wheelThumbFailed, setWheelThumbFailed] = useState(false);
  const wheelThumbSrc = wheel && !wheelThumbFailed ? wheel : ASSETS.PrimaryWheelPlaceholder;
  const isPlaceholderWheel = !wheel || wheelThumbFailed;
  const handleWheelError = (e) => {
    e.target.onerror = null;
    setWheelThumbFailed(true);
    e.target.src = ASSETS.PrimaryWheelPlaceholder;
  };

  // EW-1143: `text`/`description` is untrusted raw server HTML (actor name inside
  // <b> tags). renderSafeDescription preserves the intended bolding but routes ALL
  // text through React's escaping text nodes (never innerHTML), so an injected
  // display name like `<img src=x onerror=...>` is inert text, not an exec vector.
  const renderTextWithBold = renderSafeDescription;

  const nav = useNavigate()

  const activate = () => {
    // notif-routing (2026-07-25): resolve the destination from the AUTHORITATIVE
    // type->route map. `wheelId` here is the notification ref_id — a wheel id for
    // wheel types, but for a FOLLOW it is the FOLLOWED user (the RECIPIENT, i.e.
    // you), NOT the follower. So we also pass `actorUserId` (BE from_user): the
    // follow routes to the FOLLOWER's profile, never to your own. The resolver
    // returns null when there is no meaningful target (unknown/unmapped type, a
    // missing ref_id, or a follow with no actor id) — in that case we DON'T
    // navigate to a fallback AND DON'T mark the row read, so a click that can't
    // route never silently buries the notification (the previous /settings
    // fallback + eager mark-read data-loss).
    const dest = getNotificationRedirectUrl(type, wheelId, actorUserId, extras);
    if (!dest) return;
    // Mark read only now that we have a real destination to send the user to.
    // onActivate is a no-op when the container didn't wire it (OFF path unchanged).
    if (onActivate) onActivate();
    nav(dest);
  };

  // Wheel-Frame convergence (Gordon law all-avatars-wheel-frames): a REAL person
  // actor (not a special-type system icon like warning/tax) renders inside the
  // canonical <UserImage> wheel frame. Prefer the BE #1434 actor_profile_image;
  // UserImage supplies the framed placeholder + dead-URL onError fallback itself.
  const showFramedActor = AVATAR_WHEEL_FRAMES_ENABLED && !isSpecialType;
  // QA-1015: when the actor has no avatar (both actorProfileImage and image
  // empty), fall the framed-avatar path back to the SAME canonical iYuga logo
  // placeholder the rest of the app uses (ASSETS.wheelLogoFallback), so the
  // wheel-frame wraps the iYuga logo — not UserImage's default userPlaceholder.
  const personAvatar = actorProfileImage || image || ASSETS.wheelLogoFallback;
  const goToActor =
    actorUserId != null
      ? (e) => {
          if (e && e.stopPropagation) e.stopPropagation();
          nav(`/gallery/${actorUserId}`);
        }
      : undefined;

  // EW-nemz-G20: the actor avatar links to the actor's public gallery. #2122 wired
  // that as a bare `onClick` on the <UserImage> div — mouse-only, invisible to
  // keyboard + screen-reader users. Wrap it in a real, focusable role="link" with
  // an Enter/Space handler and a descriptive label (built from the plain-text
  // actor name only, never the raw HTML text). The row's own nav stays intact:
  // goToActor stopPropagation keeps the avatar activation from also firing the row.
  const actorLinkLabel = actorName
    ? `View ${actorName}'s profile`
    : (name ? `View ${name}'s profile` : 'View profile');
  const onActorKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToActor(e);
    }
  };

  // title-html-strip (Gordon, live staging, 2026-07-21): the stored wheel title can
  // contain junk rich-text HTML — the "Wheel Name" field is a Froala rich-text
  // editor, so a saved title is a model like "<p>Copy of LeapScholar Template</p>".
  // #2123 already routes the notification DESCRIPTION safely, but the wheelName line
  // (and the aria label built from it) rendered the raw value: React escapes it, so
  // a user SAW the literal "<p>…</p>" tags. Flatten to clean visible text with the
  // SAME inert stripper this row already uses for the untrusted description
  // (inertHtmlToText: a <template> parse — tags stripped, entities decoded, no
  // innerHTML, no script/img exec). Guard on truthiness so a system notification
  // (no wheel) still renders nothing here, never a bogus fallback label.
  const cleanWheelNameRaw = wheelName ? inertHtmlToText(wheelName) : '';
  // notif-comp-vY-001 (measured on main.e0540a60.js): a clone/share/etc. row for
  // an untitled or deleted wheel rendered this second line BLANK, next to
  // sibling rows that show a wheel name -- the row looked broken/less
  // actionable. Fall back to the SAME "Untitled Wheel" label every other
  // wheel-name surface already uses (utils/helpers.js UNTITLED_WHEEL_NAME:
  // gallery grid/list, Segment Library, name-field placeholder) -- kept as a
  // local literal rather than importing utils/helpers.js, which drags in the
  // segmentTools/Froala module graph NotificationItem.test.jsx already mocks
  // around for UserActions. Gated on isWheelNotification so a non-wheel row
  // (e.g. FOLLOW), which never had a wheel-name line, stays exactly as-is.
  const cleanWheelName = isWheelNotification(type)
    ? cleanWheelNameRaw || 'Untitled Wheel'
    : cleanWheelNameRaw;

  // bell-panel-390-secondary-line-truncation: trim this line's OWN displayed
  // text to a word boundary before the native clamp ever has to cut it -- see
  // useWordSafeLineClamp for why (the clamp's ellipsis is character-based,
  // not word-based, regardless of word-break/overflow-wrap).
  const [wheelNameRef, wheelNameDisplay] = useWordSafeLineClamp(cleanWheelName);

  // EW-nemz509: the row is a click target, so it must be reachable + operable by
  // keyboard and announced as a button (was a bare <div onClick> — invisible to
  // keyboard + screen-reader users). aria-label is built from plain-text fields
  // only (never the raw HTML `text`) — cleanWheelName so a screen reader never
  // reads out literal "<p>" tags either.
  //
  // notif-arialabel-wrongfield (gauntlet-realtime3, N5.10, 2026-08-06): the label
  // was built from `name` + `cleanWheelName` + `time` only. MEASURED LIVE on
  // web-stg: `name` (`notification.name`) is the RECIPIENT's own display name on
  // every row (not the actor, not the action), and non-wheel notifications (e.g.
  // FOLLOW) carry no `wheelName` either — so three different follow
  // notifications (three different actors: Gordon Maniskas, iYugaEdited
  // FreeQA2, QAFree Tester) all announced IDENTICALLY as "QAEssentialTester 1
  // week ago". A screen-reader user could not tell the rows apart or learn what
  // happened. `text` (description) is the field that already varies correctly
  // per row and is what sighted users see rendered ("Gordon Maniskas started
  // following you"); fold its inert plain-text extraction (same helper already
  // used for the wheel-title label) into the accessible name so it always
  // carries the real, distinguishing action sentence. `name` is kept (existing
  // callers/tests rely on it as the leading, sometimes-correct actor label) —
  // this is additive, not a replacement, so no caller's contract changes.
  const plainSentence = text ? inertHtmlToText(text) : '';
  const rowLabel =
    [name, plainSentence, cleanWheelName, displayTime].filter(Boolean).join(" ").trim() || "Notification";

  // QA-1014: kebab-menu wiring.
  //  · Report defers to the container's onReport (which opens the app's SHARED
  //    redux report flow against the ACTOR — there is no notification-report BE
  //    endpoint, and none is needed). Shown only when the row has a real actor
  //    AND the container wired a handler.
  //  · Delete + Mark-as-read defer to the container's react-query mutations.
  const canReport = actorUserId != null && !isSpecialType && typeof onReport === 'function';
  const canDelete = typeof onDelete === 'function' && notificationId != null;
  const canMarkReadMenu = isUnread && typeof onMarkRead === 'function' && notificationId != null;
  // Only mount the kebab when the container actually wired at least one action —
  // so a bare presentational render (no handlers, e.g. legacy surfaces/unit tests)
  // stays byte-identical (one row, no extra button), while the live dropdown that
  // wires delete/mark-read/report always shows it.
  const showKebab = canDelete || canMarkReadMenu || canReport;
  const menuLabel = actorName || name || '';
  const handleReport = () => {
    if (onReport) onReport();
  };
  const handleDeleteRow = () => {
    if (onDelete && notificationId != null) onDelete(notificationId);
  };
  const handleMarkReadRow = () => {
    if (onMarkRead && notificationId != null) onMarkRead(notificationId);
  };
  // Part 4: a FOLLOW notification is where a follow-back / message action is
  // meaningful — show Follow + Message as real stacked buttons there (full
  // variant). Every other actor row keeps the compact ⋮ affordance (unchanged),
  // so the feed never fires a per-row follow-status GET on unrelated rows.
  const isFollowType = type === NOTIFICATION_TYPES.FOLLOW.value;

  // reg-kelly-0902-notif-duplicate-kebabs-and-option-set: a non-follow actor row
  // used to render BOTH the standalone UserActions "compact" overflow (Follow/
  // Message) AND this row's own kebab (Mark as read/Report/Delete) whenever the
  // container wired the latter — two "..." triggers on one row with two
  // disjoint option sets. Once the per-row kebab exists, fold Follow/Message
  // into IT (NotificationRowMenu's actorUserId prop) instead of also mounting
  // the separate compact overflow. A row with no wired kebab (showKebab=false)
  // is unaffected — the compact affordance stays its only control.
  const mergeActorActionsIntoKebab =
    USER_ACTIONS_ENABLED && actorUserId != null && !isSpecialType && !isFollowType && showKebab;

  // kelly-notif-follow-slot-column-x-0915: on the /notifications PAGE the follow
  // row's stacked Follow + Message column is rendered INSIDE the wheel-thumbnail
  // column instead of as an extra in-flow sibling beside it — see
  // RightActionsColumn's `inSlot` comment for the measured 78px the sibling form
  // stole from the row's flex line (and therefore from every follow row's
  // column x). The bell dropdown is untouched: RightSection does not render
  // there, so the actions stay a direct sibling exactly as before.
  const showFollowActions =
    USER_ACTIONS_ENABLED && actorUserId != null && !isSpecialType && isFollowType;
  const followActionsInSlot = showFollowActions && !isDropDown;

  return (
    <NotificationContainer
      isWarning={isWarning}
      isUnread={isUnread}
      role="button"
      tabIndex={0}
      aria-label={rowLabel}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          activate();
        }
      }}
    >
      <LeftSection>
        <ImageContainer>
          {showFramedActor ? (
            goToActor ? (
              <span
                role="link"
                tabIndex={0}
                aria-label={actorLinkLabel}
                onClick={goToActor}
                onKeyDown={onActorKeyDown}
                style={{ cursor: 'pointer', display: 'inline-flex', borderRadius: '50%' }}
              >
                <UserImage image={personAvatar} size={50} alt={name || 'user'} />
              </span>
            ) : (
              <UserImage image={personAvatar} size={50} alt={name || 'user'} />
            )
          ) : (
            <ProfileImage src={avatarSrc} alt={name || type} onError={handleAvatarError} />
          )}
          {type && iconMap[type] && <IconOverlay>{iconMap[type]}</IconOverlay>}
        </ImageContainer>
        <TextContainer>
          {/* N-08 residual (measured live web-stg 2026-07-28, both surfaces): #2271
              clamped the WHEEL-TITLE line below, but this — the primary message
              line — stayed unbounded. Measured in the 380px bell panel: a long
              actor name + message grew this <p> to 144px (6 lines) and the row to
              229px, so barely one notification fitted in the panel. (`subTitle` is
              not a mapped TextView variant — capital T; the mapped one is
              `subtitle` — so it inherits none of TextView's base clamp CSS.)
              Clamp to 2 lines with the SAME treatment the sibling title line
              already uses, per the deck's row spec (`.nd-title{-webkit-line-clamp:2}`
              in concepts/notification-center-elevated/02-bell-dropdown-grid.html).
              Measured with the clamp applied on the live build: message 144px -> 48px,
              row 229px -> 157px, still truncating (scrollHeight > clientHeight).
              The full message stays reachable on the destination the row links to,
              and the screen-reader row label is built from plain fields, unchanged. */}
          <TextView
            variant="subTitle"
            style={{
              fontWeight: '500',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {NOTIF_PLAIN_NAME_ENABLED ? (
              // #1434 plain-name render: sentence built from structured fields —
              // actor_name (plain) bolded + entity-decoded action phrase. No HTML
              // `description` parse, so no literal <p>/<b> tags and no apostrophe
              // entity artifact; a hostile display name renders as inert text.
              renderNotificationSentence({ actorName, description: text })
            ) : (
              <>
                {name && (
                  <>
                    {name}
                  </>
                )}
                <span style={{ color: '#696F8C' }}>{renderTextWithBold(text)}</span>
              </>
            )}
          </TextView>
          {/* N-05 (measured live, wave6 2026-07-24): a long wheel title wrapped to
              ~7 lines, pushing the other rows out so the dropdown showed only 2 of 4
              notifications. The "subTitile" variant is unmapped in TextView, so none
              of its base clamp CSS applied and the title wrapped unbounded. Clamp the
              title to 2 lines with an ellipsis so one long title can never crowd out
              the rest of the feed. Full title stays reachable on the wheel it links
              to; screen-reader label already uses the plain-text cleanWheelName. */}
          <TextView
            ref={wheelNameRef}
            variant="subTitile"
            style={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {wheelNameDisplay}
          </TextView>
          {isDropDown && showActionButtons && (
            <ActionButtonsContainer>
              <StyledButton variant="accept">Accept</StyledButton>
              <StyledButton variant="deny">Deny</StyledButton>
            </ActionButtonsContainer>
          )}
          <TextView variant="text">{displayTime}</TextView>
        </TextContainer>
      </LeftSection>

      {/* N-03 (measured live, ov-close-notif 2026-07-26): comment rows also lacked
          the wheel thumbnail that clone/favorite rows show, because this list was a
          hardcoded subset (types 1-6) that omitted COMMENT (16) and AI_WHEEL (15).
          Key off the authoritative wheel-instance predicate instead so every
          wheel-instance notification renders its thumbnail consistently.

          notif-thumb-inconsistent (MEASURED live web-stg, app-stg notification API
          2026-08-15, user_id 21): the render was ALSO gated on `wheel &&` (the
          thumbnail image being non-empty), so a wheel notification whose backend
          `image` is empty — e.g. every "Someone unlocked your Wheel" (UNLOCK, type
          5) row for a freshly cloned wheel that has no `wheel_preview.png` raster
          yet, and equally a CLONE row for such a wheel — rendered NO thumbnail at
          all, sitting next to favourite/clone rows that DID show one. UNLOCK is
          already in WHEEL_INSTANCE_TYPES, so this was never a type-drop; it was the
          empty-image guard. Render the slot for EVERY wheel-instance row and degrade
          a missing/empty raster to the canonical wheel placeholder — the SAME
          fallback the onError handler already uses for a dead URL — so every wheel
          row carries a consistent 60x60 thumbnail (placeholder, never a broken
          glyph). Non-wheel types (follow/system) still render nothing here.

          kelly-0902-notif-viewall-row-right-side-order (Kelly Ellerbrake, Slack
          #dev 2026-09-02): on the View All full page (isDropDown=false) the
          right-hand rail must read wheel thumbnail -> actor kebab -> unread
          bubble, left to right. This block now renders BEFORE the actor-actions
          slot below (was after) so the wheel thumbnail lands first in DOM/flex
          order. The bell dropdown is unaffected — RightSection never renders
          there (isDropDown=true), so moving its position relative to a sibling
          that also doesn't affect the dropdown's own layout.

          kelly-notif-follow-row-no-wheel-slot-0915: a non-wheel-instance row
          (e.g. FOLLOW) used to render NOTHING here at all, so the right-hand
          column had no fixed edge to line up against on those rows. The
          RightSection wrapper now always renders on the page; only the
          <WheelImage> inside it is conditional on isWheelNotification(type) —
          a non-wheel row gets an equal-size invisible WheelSlotSpacer instead,
          so the column is a straight vertical edge for every row type. */}
      {!isDropDown && (
          <RightSection data-testid="notif-wheel-column">
            {isWheelNotification(type) ? (
              <WheelImage
                src={wheelThumbSrc}
                /* Describe the WHEEL (not the actor). The thumbnail previously
                   borrowed the actor's `name` as its alt, which both duplicated the
                   avatar's accessible name (two "Gordon" images on one row) and
                   mislabelled the wheel preview. Use the clean wheel title so a
                   screen reader announces the thumbnail as the wheel it depicts. */
                alt={cleanWheelName ? `${cleanWheelName} wheel` : 'Wheel'}
                onError={handleWheelError}
                data-testid="wheel-thumb"
                data-placeholder-wheel={isPlaceholderWheel ? 'true' : undefined}
              />
            ) : followActionsInSlot ? (
              /* kelly-notif-follow-slot-column-x-0915: a follow row has no wheel
                 to show, so its Follow + Message control IS this column's
                 content — the same 60px box a wheel row's thumbnail occupies.
                 That keeps the column a straight vertical edge without
                 reserving dead space on wheel rows. */
              <RightActionsColumn
                inSlot
                data-actor-actions="true"
                data-testid="notif-follow-actions"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <UserActions
                  userId={actorUserId}
                  userLabel={actorName || name || ''}
                  variant="full"
                  stack
                />
              </RightActionsColumn>
            ) : (
              <WheelSlotSpacer aria-hidden="true" data-testid="wheel-thumb-spacer" />
            )}
          </RightSection>
        )}
      {/* Follow + Message on the actor (BE #1434 actor_user_id). "Most places you
          see someone's avatar you can follow/message them." Compact ⋮ variant:
          dense notification rows can't fit two inline buttons, and compact defers
          the follow-status fetch until the menu opens (no per-row status GET on a
          long feed). Two DISTINCT controls — Follow toggles the graph, Message
          get-or-creates the DM — never conflated (UserActions owns that). The
          one-way-follow DM policy is enforced inside UserActions + server-side. */}
      {USER_ACTIONS_ENABLED && actorUserId != null && !isSpecialType && (
        isFollowType ? (
          // Part 4: Follow + Message stacked on the RIGHT for follow rows.
          // kelly-notif-follow-slot-column-x-0915: on the PAGE this control is
          // rendered inside the wheel column above (followActionsInSlot), so
          // this sibling form is now the BELL DROPDOWN's only — unchanged there.
          !followActionsInSlot && (
          <RightActionsColumn
            data-actor-actions="true"
            data-testid="notif-follow-actions"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <UserActions
              userId={actorUserId}
              userLabel={actorName || name || ''}
              variant="full"
              stack
            />
          </RightActionsColumn>
          )
        ) : (
          !mergeActorActionsIntoKebab && (
            <ActorActionsSlot
              /* Stable hook for any container that dismisses itself when a row is
                 activated (the NotificationCenter bell panel does): activating this
                 slot opens a menu IN PLACE, it does not navigate, so the container
                 must not treat it as "the user left". Marked here rather than
                 sniffing UserActions' internals. */
              data-actor-actions="true"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <UserActions
                userId={actorUserId}
                userLabel={actorName || name || ''}
                variant="compact"
              />
            </ActorActionsSlot>
          )
        )
      )}
      {/* kelly-notif-page-thumb-column-ragged-12px-0915: always mount the dot's
          12px box (visibility toggles, layout doesn't) — see the styled
          component's comment for why a conditionally-MOUNTED dot shifted
          every sibling to its right by 12px on read rows. */}
      <GreenDot isUnread={isUnread} />

      {/* QA-1014 parts 1/2/3: on-hover kebab in the UPPER-RIGHT corner. Menu =
          Mark as read (unread only) + Report (actor rows) + Delete. */}
      {showKebab && (
        <NotificationRowMenu
          isUnread={canMarkReadMenu}
          canReport={canReport}
          onMarkRead={handleMarkReadRow}
          onReport={handleReport}
          onDelete={handleDeleteRow}
          label={menuLabel}
          actorUserId={mergeActorActionsIntoKebab ? actorUserId : undefined}
        />
      )}
    </NotificationContainer>
  );
}

export default NotificationItem;
